import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import HomeContent from './components/HomeContent';
import AlbumsContent from './components/AlbumsContent';
import About from './components/About';
import Contact from './components/Contact';
import { PRIMARY_FONT } from './constants';
import './styles/Notebook.css';

// Generate crooked lines for a repeating SVG pattern tile
// 8 unique hand-drawn lines that tile seamlessly across the canvas
const generatePatternLines = () => {
  const lines = [];
  const lineSpacing = 34;
  const linesPerTile = 8;

  for (let i = 0; i < linesPerTile; i++) {
    const y = (i + 1) * lineSpacing;
    const seed = i * 7 + 3;
    const skew = ((seed * 13) % 7 - 3) * 0.15;
    const wave1 = ((seed * 17) % 5 - 2) * 0.4;
    const wave2 = ((seed * 23) % 5 - 2) * 0.3;
    const wave3 = ((seed * 31) % 5 - 2) * 0.35;

    const d = `M 0 ${y + skew} Q 1000 ${y + wave1}, 2000 ${y + wave2} T 3000 ${y + wave3} T 4000 ${y + skew}`;
    lines.push(d);
  }
  return lines;
};

const patternLines = generatePatternLines();

// 2x2 grid layout — translate canvas by -50% to show each quadrant
// x/y percentages are relative to the element's own size (200% of viewport)
const pageOffset = {
  home:    { x: '0%',   y: '0%' },
  albums:  { x: '-50%', y: '0%' },
  about:   { x: '0%',   y: '-50%' },
  contact: { x: '-50%', y: '-50%' },
};

const App = () => {
  const [activePage, setActivePage] = useState('home');
  const [activeTag, setActiveTag] = useState('all');

  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100vh';
    document.body.style.overflow = 'hidden';
    document.body.style.fontFamily = PRIMARY_FONT;
  }, []);

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      <Header />

      {/* Content area — clips the 2x2 canvas */}
      <div className="flex-grow relative overflow-hidden">
        <motion.div
          className="absolute"
          style={{ width: '200%', height: '200%' }}
          animate={pageOffset[activePage]}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* SVG notebook lines — tiled pattern across entire canvas */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ zIndex: 1 }}
          >
            <defs>
              <pattern
                id="notebook-lines"
                x="0" y="0"
                width="4000" height="272"
                patternUnits="userSpaceOnUse"
              >
                {patternLines.map((d, i) => (
                  <path
                    key={i}
                    d={d}
                    stroke="rgba(255,255,255,0.04)"
                    strokeWidth="1"
                    fill="none"
                  />
                ))}
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#notebook-lines)" />
          </svg>

          {/* Red margin lines — one per column, moves with the canvas */}
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: '48px', width: '1px', background: 'rgba(220, 60, 60, 0.15)', zIndex: 2 }}
          />
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: 'calc(50% + 48px)', width: '1px', background: 'rgba(220, 60, 60, 0.15)', zIndex: 2 }}
          />

          {/* 2×2 page grid */}
          <div className="grid grid-cols-2 grid-rows-2 w-full h-full relative" style={{ zIndex: 5 }}>
            {/* Home — top left */}
            <div className="overflow-y-auto min-h-0 pb-6">
              <HomeContent />
            </div>

            {/* Albums — top right */}
            <div className="overflow-y-auto min-h-0 p-10 pb-6">
              <AlbumsContent activeTag={activeTag} setActiveTag={setActiveTag} />
            </div>

            {/* About — bottom left */}
            <div className="overflow-y-auto min-h-0 p-10 pb-6">
              <About />
            </div>

            {/* Contact — bottom right */}
            <div className="overflow-y-auto min-h-0 p-10 pb-6">
              <Contact />
            </div>
          </div>
        </motion.div>
      </div>

      <Footer activePage={activePage} setActivePage={setActivePage} />
    </div>
  );
};

export default App;
